import { APP_VERSION_HEADER, compareAppVersions } from './app-version'
import {
  CLIENT_FEATURE_COURSE_AXIS,
  CLIENT_FEATURE_TAXONOMY_V2,
  CLIENT_FEATURES_HEADER,
  hasClientFeature,
} from './schemas/simulation'
import { TAXONOMY, type Axis } from './taxonomy'

/**
 * 1.0.3·1.0.4 앱이 아는 분류 (5556dee 의 taxonomy.ts 에서 뽑아 박제, 대분류 31 + 세부 248 = 279).
 * 이 표는 고치지 않는다. 새 대분류·세부는 taxonomy.ts 에만 더한다.
 * 옛 앱은 모르는 id 를 그대로 글자로 보여 준다("intl · intl.pasta-dry"). 그래서 서버는 X-Client-Features 에
 * taxonomy-v2 가 없는 요청의 응답에서 이 표에 없는 id 를 null 로 낮춘다 (legacySafeTaxonomy). 1.0.5 판·1.0.6~1.0.8 판·1.0.9 판은 아래 TAXONOMY_KEYS_1_0_5·TAXONOMY_KEYS_1_0_6·TAXONOMY_KEYS_1_0_9.
 */
const LEGACY: Record<string, readonly string[]> = {
  'product:sauce': [
    'liquid',
    'ramen-soup',
    'spice-blend',
    'marinade',
    'seasoning-base',
    'dipping',
    'jang',
    'worcester-steak',
    'mayo',
    'dressing',
    'pesto',
    'chutney',
    'salsa',
    'aioli',
    'tartar',
    'ssamjang',
    'bbq',
    'hot-sauce',
    'oyster',
    'bearnaise',
    'demi-glace',
    'fire-chicken',
    'cream-pasta',
    'aged-gochujang',
    'truffle-mayo',
  ],
  'product:meal-kit': [
    'soup-stew',
    'stir-fry',
    'noodle',
    'side-dish',
    'pasta',
    'hot-pot',
    'salad',
    'budae',
    'gambas',
    'mille-feuille',
    'malatang',
    'seafood-steam',
    'seafood-pancake',
    'chilled-pasta',
  ],
  'product:beverage': [
    'soda',
    'juice',
    'rtd-tea',
    'functional',
    'soda-base',
    'protein',
    'kombucha',
    'smoothie',
    'ade',
    'electrolyte',
    'plant-milk',
    'probiotic',
    'sikhye',
    'cold-brew-latte',
    'energy',
    'collagen',
    'condition',
    'zero-soda',
    'vitamin-water',
    'hpp-shot',
    'aseptic-plant-protein',
  ],
  'product:coffee': [
    'rtd',
    'cold-brew-concentrate',
    'drip-bag',
    'ground',
    'mix-stick',
    'capsule',
    'rtd-latte',
    'instant',
    'bulk-concentrate',
    'cold-brew-bag',
    'nitro',
    'specialty-drip',
  ],
  'product:alcohol': [
    'traditional',
    'fruit-wine',
    'liqueur',
    'highball',
    'craft-beer',
    'non-alcoholic-beer',
    'distilled-soju',
    'fresh-makgeolli',
    'oak-aged',
    'sparkling-traditional',
    'yuzu-yakju',
    'omija-sparkling',
  ],
  'product:misc': [
    'powder',
    'seasoning',
    'snack',
    'treat',
    'dessert',
    'bakery',
    'fermented',
    'spread',
    'syrup-topping',
    'ssamjang-tube',
  ],
  'product:frozen': ['dumpling', 'fried-rice', 'bar-snack', 'cutlet', 'noodle'],
  'product:hmr': ['retort-soup', 'cup-rice', 'porridge', 'bowl-sauce', 'instant-pasta'],
  'product:snacks': ['chips', 'bar', 'gummy', 'popcorn'],
  'product:bakery': ['premix', 'frozen-dough', 'cookie', 'cake'],
  'product:dairy': ['yogurt', 'natural-cheese', 'processed-cheese', 'ice-cream'],
  'product:meat': ['sausage', 'ham', 'nugget', 'patty', 'jerky'],
  'product:seafood': ['fish-cake', 'jeotgal', 'smoked-salmon', 'semi-dried', 'seaweed'],
  'product:supplement': ['powder-stick', 'jelly-stick', 'tablet', 'ampoule', 'gummy'],
  'product:pickled': ['kimchi', 'jangajji', 'pickle', 'fermented-pickle'],
  'product:noodles': ['fresh', 'dried', 'cooked', 'frozen', 'cup'],
  'product:canned': ['tuna', 'fruit', 'tomato', 'beans', 'soup'],
  'product:dried': [
    'freeze-dried-produce',
    'dried-greens',
    'jerky',
    'soup-block',
    'freeze-dried-coffee',
  ],
  'product:baby': ['pouch', 'puffs', 'formula', 'drink'],
  'product:sports': ['protein-powder', 'protein-bar', 'shake', 'electrolyte-powder'],
  'product:pet': ['baked', 'freeze-dried', 'wet-pouch', 'dental'],
  'product:oils': ['infused', 'blended', 'pressed', 'spread', 'shortening', 'spray', 'powdered'],
  'product:grains': [
    'cereal',
    'grain-powder',
    'soy',
    'nut-butter',
    'roasted-nuts',
    'granola-bar',
    'starch',
  ],
  'product:produce': ['jam', 'puree', 'dried', 'candied', 'paste', 'iqf'],
  'menu:main': [
    'main.grill',
    'main.wok',
    'main.braise',
    'main.steam',
    'main.fry',
    'main.pancake',
    'main.raw',
    'main.roast',
    'main.smoke',
  ],
  'menu:rice-noodle': [
    'rice-noodle.rice-bowl',
    'rice-noodle.fried-rice',
    'rice-noodle.bibimbap',
    'rice-noodle.pot-rice',
    'rice-noodle.shaped-rice',
    'rice-noodle.porridge',
    'rice-noodle.soup-noodle',
    'rice-noodle.dry-noodle',
    'rice-noodle.pan-noodle',
    'rice-noodle.cold-noodle',
  ],
  'menu:soup': [
    'soup.clear',
    'soup.bone-broth',
    'soup.gukbap',
    'soup.spicy-seafood',
    'soup.whole',
    'soup.jjigae',
    'soup.hearty',
    'soup.jeongol',
    'soup.shabu',
  ],
  'menu:side': [
    'side.banchan',
    'side.namul',
    'side.pancake',
    'side.fried',
    'side.grilled',
    'side.salad',
    'side.soup',
    'side.bread',
    'side.dip',
  ],
  'menu:anju': [
    'anju.dry',
    'anju.fried',
    'anju.skewer',
    'anju.hot-pot',
    'anju.stir-fry',
    'anju.pancake',
    'anju.raw',
    'anju.steamed',
    'anju.finger',
    'anju.finish',
  ],
  'menu:dessert': [
    'dessert.baked',
    'dessert.cake',
    'dessert.tart',
    'dessert.pastry',
    'dessert.frozen',
    'dessert.cup',
    'dessert.tteok',
    'dessert.fried',
    'dessert.plated',
  ],
  'menu:drinks': [
    'drinks.espresso',
    'drinks.filter',
    'drinks.variation',
    'drinks.non-coffee-latte',
    'drinks.tea',
    'drinks.traditional-tea',
    'drinks.juice-ade',
    'drinks.smoothie',
    'drinks.shake',
    'drinks.bottled',
  ],
}

/** 형식은 taxonomy-i18n 키와 같다: 'product:sauce', 'product:sauce:liquid', 'menu:main:main.grill' */
export const LEGACY_TAXONOMY_KEYS: ReadonlySet<string> = new Set(
  Object.entries(LEGACY).flatMap(([major, subs]) => [
    major,
    ...subs.map((sub) => `${major}:${sub}`),
  ]),
)

/**
 * 1.0.5 앱이 1.0.4 보다 더 아는 분류 (5e775c1 의 taxonomy.ts 와 위 LEGACY 의 차이, 대분류 3 + 세부 23 = 26). 이 표도 고치지 않는다.
 * 1.0.5 는 taxonomy-v2 를 싣지만 1.0.6 에서 더한 분류(떡·한과 대분류, sauce:vinegar 등 35)는 모른다 (1.0.6 점검 P1-6)
 */
const ADDED_IN_1_0_5: readonly string[] = [
  'product:sauce:ganjang',
  'product:hmr:instant-rice',
  'product:snacks:chocolate',
  'product:meat:chicken-breast',
  'product:pet:dry-food',
  'product:tea',
  'product:tea:leaf',
  'product:tea:herbal',
  'product:tea:powder',
  'product:tea:cheong',
  'product:tea:concentrate',
  'product:plant-based',
  'product:plant-based:plant-meat',
  'product:plant-based:tofu-tempeh',
  'product:plant-based:dairy-alt',
  'product:plant-based:vegan-meal',
  'product:plant-based:vegan-bakery',
  'menu:intl',
  'menu:intl:intl.pasta-dry',
  'menu:intl:intl.pasta-fresh',
  'menu:intl:intl.pasta-filled',
  'menu:intl:intl.pasta-baked',
  'menu:intl:intl.risotto-grain',
  'menu:intl:intl.pizza-flatbread',
  'menu:intl:intl.burger-sandwich',
  'menu:intl:intl.main-salad',
]

/** 1.0.5 앱이 아는 분류 (키 형식은 LEGACY_TAXONOMY_KEYS 와 같다) */
const TAXONOMY_KEYS_1_0_5: ReadonlySet<string> = new Set([
  ...LEGACY_TAXONOMY_KEYS,
  ...ADDED_IN_1_0_5,
])

/**
 * 1.0.6~1.0.8 앱이 1.0.5 보다 더 아는 분류 (2026-10-23 확장, 대분류 1 + 식품 세부 30 + 메뉴 세부 4 = 35). 이 표도 고치지 않는다.
 * 이 판은 2026-11-01 메뉴 보강(초밥·타파스·국제·서양 메인 묶음 세부 18)을 모른다
 */
const ADDED_IN_1_0_6: readonly string[] = [
  'product:sauce:ramen-flakes',
  'product:sauce:ramen-oil',
  'product:sauce:vinegar',
  'product:meal-kit:tteokbokki',
  'product:beverage:juice-concentrate',
  'product:beverage:health-juice',
  'product:alcohol:gin-vodka',
  'product:misc:honey',
  'product:frozen:ice-pop',
  'product:frozen:snack',
  'product:hmr:banchan',
  'product:hmr:dosirak',
  'product:snacks:candy',
  'product:bakery:bread',
  'product:dairy:butter-cream',
  'product:dairy:pudding',
  'product:meat:marinated',
  'product:meat:jokbal',
  'product:meat:egg',
  'product:seafood:soy-marinated',
  'product:seafood:dried-seasoned',
  'product:supplement:red-ginseng',
  'product:canned:vegetable',
  'product:grains:muk',
  'product:tteok',
  'product:tteok:steamed',
  'product:tteok:pounded',
  'product:tteok:filled',
  'product:tteok:cooking',
  'product:tteok:hangwa',
  'product:tteok:yanggaeng',
  'menu:rice-noodle:rice-noodle.tteokbokki',
  'menu:rice-noodle:rice-noodle.dumpling',
  'menu:intl:intl.egg-brunch',
  'menu:drinks:drinks.cocktail',
]

/** 1.0.6~1.0.8 앱이 아는 분류 */
const TAXONOMY_KEYS_1_0_6: ReadonlySet<string> = new Set([
  ...TAXONOMY_KEYS_1_0_5,
  ...ADDED_IN_1_0_6,
])

/** 1.0.6 판(TAXONOMY_KEYS_1_0_6)이 처음 실린 앱 버전 */
const TAXONOMY_1_0_6_SINCE_APP_VERSION = '1.0.6'

/**
 * 1.0.9 판(TAXONOMY_KEYS_1_0_9)이 처음 실린 앱 버전. 2026-11-01 메뉴 보강·식품 명세 대조·코스 축은 1.0.8(빌드 21·22)에
 * 실리지 않고 1.0.9(빌드 23)에 실렸다 (release/1.0.9-candidate-20261101). 1.0.8 이하는 새 세부를 대분류만, 장류는 분류 없이 본다
 */
const TAXONOMY_1_0_9_SINCE_APP_VERSION = '1.0.9'

/**
 * 이 앱 버전부터 지금 taxonomy.ts 를 다 안다. taxonomy.ts 에 분류를 더하면 직전 앱 버전이 아는 키 집합을 위처럼 박제하고,
 * clientTaxonomyKeys 에 그 판을 넣은 뒤 이 값을 새 분류가 처음 실린 앱 버전으로 올린다.
 * 역할마다 나눈 코스 스타일(2026-11-01 점검 1·2번)과 주류·건조 세분(같은 날 전수 확인 뒤)은 1.0.10 에 실린다
 * (release/1.0.10-candidate-20261101)
 */
const FULL_TAXONOMY_SINCE_APP_VERSION = '1.0.10'

/**
 * 코스·테이스팅 축(2026-11-01)을 아는 판의 키 집합이 담는 축 키. 식품·메뉴 축은 1.0.3 부터 있어 축 키를 두지 않는다.
 * 코스 축을 아는 판(TAXONOMY_KEYS_1_0_9)에 들어 있다
 */
const COURSE_AXIS_KEY = 'course'

/**
 * 1.0.9 앱(빌드 23)이 1.0.6~1.0.8 보다 더 아는 식품·메뉴 분류 (2026-11-01 메뉴 보강·식품 명세 대조, 대분류 1 + 식품 세부 19 + 메뉴 세부 18 = 38).
 * main 925c6584(= release/1.0.10-candidate dcf97bde)의 taxonomy.ts 와 TAXONOMY_KEYS_1_0_6 의 차이. 이 표도 고치지 않는다.
 * 1.0.9 는 1.0.10 에서 더한 주류·건조 세분(세부 21)을 모른다
 */
const ADDED_IN_1_0_9: readonly string[] = [
  'product:meal-kit:other',
  'product:frozen:iqf',
  'product:frozen:other',
  'product:snacks:protein-snack',
  'product:seafood:other',
  'product:pickled:jeotgal',
  'product:noodles:instant-rice',
  'product:dried:other',
  'product:sports:high-protein',
  'product:tea:rtd',
  'product:tea:functional',
  'product:tea:kombucha',
  'product:plant-based:plant-milk',
  'product:plant-based:plant-protein',
  'product:jang',
  'product:jang:gochujang',
  'product:jang:doenjang',
  'product:jang:ganjang',
  'product:jang:ssamjang',
  'product:jang:other',
  'menu:main:main.sushi',
  'menu:intl:intl.risotto',
  'menu:intl:intl.orzo',
  'menu:intl:intl.farro',
  'menu:intl:intl.quinoa-bowl',
  'menu:intl:intl.pizza-napoli',
  'menu:intl:intl.pizza-roman',
  'menu:intl:intl.pizza-al-taglio',
  'menu:intl:intl.focaccia',
  'menu:intl:intl.flatbread',
  'menu:intl:intl.burger',
  'menu:intl:intl.club-sandwich',
  'menu:intl:intl.open-sandwich',
  'menu:intl:intl.baguette-sandwich',
  'menu:intl:intl.salad-protein',
  'menu:intl:intl.salad-grain',
  'menu:intl:intl.salad-warm',
  'menu:side:side.tapas',
]

/**
 * 1.0.9 앱이 아는 식품·메뉴 분류. 1.0.9 이상인데 코스 축을 모르는 빌드(X-Client-Features 에 course-axis 가 없다)도 이 판을 받는다.
 * 코스 축이 메뉴 보강과 다른 판에 실려도 그 빌드가 코스 접시에서 깨지지 않게 버전이 아니라 기능 헤더로 가른다.
 * 1.0.6~1.0.8 은 그 앞에서 TAXONOMY_KEYS_1_0_6 을 받는다 (그 판에도 코스 키가 없어 코스 축을 낮춘다)
 */
const KEYS_BEFORE_COURSE_AXIS: ReadonlySet<string> = new Set([
  ...TAXONOMY_KEYS_1_0_6,
  ...ADDED_IN_1_0_9,
])

/**
 * 1.0.9 앱(빌드 23)이 아는 코스 세부의 끝 조각. 역할 10개가 모두 같은 14칸이었다 (명세 1장 기법 7 + 2장 국제 묶음 7).
 * 이 표는 고치지 않는다. 1.0.10 은 역할마다 스타일을 나눴고, 이 14칸은 지금도 모든 역할에 남아 있다 (숨긴 칸 포함)
 */
const COURSE_STYLES_1_0_9: readonly string[] = [
  'sous-vide',
  'confit',
  'fermentation',
  'curing',
  'smoking',
  'aging',
  'modernist',
  'pasta',
  'risotto-grain',
  'pizza',
  'burger-sandwich',
  'main-salad',
  'tapas',
  'sushi',
]

/**
 * 1.0.9 앱이 아는 분류: 1.0.9 의 식품·메뉴 축(KEYS_BEFORE_COURSE_AXIS), 코스 축 키, 코스 역할 10개와 역할마다 위 14칸.
 * 1.0.10 에서 더한 주류·건조 세분은 대분류만, 역할마다 나눈 코스 스타일은 역할만 보인다
 */
const TAXONOMY_KEYS_1_0_9: ReadonlySet<string> = new Set([
  ...KEYS_BEFORE_COURSE_AXIS,
  COURSE_AXIS_KEY,
  ...TAXONOMY.course.flatMap((role) => [
    `course:${role.id}`,
    ...COURSE_STYLES_1_0_9.map((leaf) => `course:${role.id}:${role.id}.${leaf}`),
  ]),
])

/**
 * 요청한 앱이 아는 분류 키 집합. null 이면 지금 분류를 다 안다 (낮추지 않는다).
 *  - taxonomy-v2 가 없다 (1.0.3·1.0.4, 헤더 없는 옛 앱·도구) → LEGACY_TAXONOMY_KEYS
 *  - taxonomy-v2 는 있는데 스토어 버전이 1.0.6 미만 (1.0.5) → TAXONOMY_KEYS_1_0_5.
 *    1.0.5 와 1.0.6 은 같은 기능 값을 보내서 X-App-Version(1.0.3~) 으로만 가른다
 *  - 1.0.6 이상 1.0.9 미만 (1.0.6~1.0.8) → TAXONOMY_KEYS_1_0_6 (메뉴 보강·코스 축을 모른다)
 *  - 1.0.9 이상인데 course-axis 가 없다 (코스 축 전 빌드) → KEYS_BEFORE_COURSE_AXIS (코스 축과 1.0.10 의 주류·건조 세분을 모른다)
 *  - 1.0.9 이상 FULL_TAXONOMY_SINCE_APP_VERSION 미만 (1.0.9) → TAXONOMY_KEYS_1_0_9 (1.0.10 의 주류·건조 세분과 역할마다 나눈 코스 스타일을 모른다)
 *  - 버전 헤더가 없거나 형식이 틀리면(Expo Go·웹 = 지금 코드) 다 안다고 본다
 * header 는 요청 헤더 읽기 (API 는 name => c.req.header(name))
 */
export function clientTaxonomyKeys(
  header: (name: string) => string | undefined,
): ReadonlySet<string> | null {
  const features = header(CLIENT_FEATURES_HEADER)
  if (!hasClientFeature(features, CLIENT_FEATURE_TAXONOMY_V2)) {
    return LEGACY_TAXONOMY_KEYS
  }
  const version = header(APP_VERSION_HEADER)?.trim()
  if (!version) return null
  if (compareAppVersions(version, TAXONOMY_1_0_6_SINCE_APP_VERSION) === -1) {
    return TAXONOMY_KEYS_1_0_5
  }
  if (compareAppVersions(version, TAXONOMY_1_0_9_SINCE_APP_VERSION) === -1) {
    return TAXONOMY_KEYS_1_0_6
  }
  if (!hasClientFeature(features, CLIENT_FEATURE_COURSE_AXIS)) return KEYS_BEFORE_COURSE_AXIS
  if (compareAppVersions(version, FULL_TAXONOMY_SINCE_APP_VERSION) === -1) {
    return TAXONOMY_KEYS_1_0_9
  }
  return null
}

/** 그 앱이 이 축을 아는가. known 은 clientTaxonomyKeys 값 (null = 다 안다) */
export function knowsTaxonomyAxis(axis: Axis, known: ReadonlySet<string> | null): boolean {
  return !known || axis !== 'course' || known.has(COURSE_AXIS_KEY)
}

/**
 * 뜻이 좁아진 세부 2개의 옛 이름·hint (5556dee 그대로). 1.0.3·1.0.4 앱에서는 '장류 (발효장)' 으로 고추장을,
 * '죽·리조또' 로 리조또를 만든다. 그 요청의 프롬프트에 새 이름("된장·기타 발효장", "리조또는 국제·서양 메인")이
 * 들어가면 모델이 사용자 설명과 반대로 끌려간다 (반박 검토 M4). 서버는 taxonomy-v2 를 모르는 앱이 만든 행에 이 값을 쓴다.
 * 키 형식은 LEGACY_TAXONOMY_KEYS 와 같다
 */
const LEGACY_PROMPT_SUBS: Record<string, { ko: string; hint?: string }> = {
  'product:sauce:jang': { ko: '장류 (발효장)' },
  'menu:rice-noodle:rice-noodle.porridge': {
    ko: '죽·리조또',
    hint: '전분을 수분에 풀어 익히는 형태. 물 비율과 계속 저어야 하는 시간이 화구를 길게 점유. 농도(되기)와 미리 쑤어 둘 수 있는지가 질문, 저작 부담이 낮아 연령·회복식 타깃. 예: 전복죽, 야채죽, 버섯리조또, 해물리조또',
  },
}

/** 옛 앱이 만든 요청의 프롬프트용 세부 이름·hint. 뜻이 바뀌지 않은 세부는 undefined (지금 값을 쓴다) */
export function legacyPromptSub(
  axis: Axis,
  majorId: string | null,
  subId: string | null,
): { ko: string; hint?: string } | undefined {
  if (!majorId || !subId) return undefined
  return LEGACY_PROMPT_SUBS[`${axis}:${majorId}:${subId}`]
}

/**
 * 옛 앱이 모르는 대분류면 둘 다 null, 대분류는 알고 세부만 모르면 subId 만 null.
 * known 은 그 앱이 아는 키 집합 (clientTaxonomyKeys). 기본은 1.0.3·1.0.4 판
 */
export function legacySafeTaxonomy(
  axis: Axis,
  majorId: string | null,
  subId: string | null,
  known: ReadonlySet<string> = LEGACY_TAXONOMY_KEYS,
): { majorId: string | null; subId: string | null } {
  if (!majorId || !known.has(`${axis}:${majorId}`)) return { majorId: null, subId: null }
  if (!subId) return { majorId, subId: null }
  return { majorId, subId: known.has(`${axis}:${majorId}:${subId}`) ? subId : null }
}

/**
 * 축을 모르는 앱(1.0.8 까지가 받은 코스 접시)에 보낼 사본: 분류 없는 메뉴. 그 앱은 모르는 축의 분류 표를 찾다 화면이 깨지고,
 * 메뉴면 1인분 라벨로 그린다. 아는 축이면 같은 객체. 표시용 응답에만 쓴다. 저장값은 바꾸지 않는다
 */
export function withKnownAxis<
  T extends { axis?: Axis | null; majorId?: string | null; subId?: string | null },
>(value: T, known: ReadonlySet<string> | null): T {
  if (knowsTaxonomyAxis(value.axis ?? 'product', known)) return value
  return { ...value, axis: 'menu' as const, majorId: null, subId: null }
}

/**
 * 응답 객체(글 제품 스냅샷·제품 목록 행·제품 상세 input)의 majorId·subId 를 그 앱이 아는 분류로 낮춘 사본.
 * known 은 clientTaxonomyKeys 값, null 이면 그대로. 분류 칸이 없거나 이미 아는 값이면 같은 객체를 돌려준다 — 칸을 새로 만들지 않는다.
 * 축을 모르면 withKnownAxis 처럼 분류 없는 메뉴로 보낸다. 표시용 응답에만 쓴다. 저장값은 바꾸지 않는다
 */
export function withKnownTaxonomy<
  T extends { axis?: Axis | null; majorId?: string | null; subId?: string | null },
>(value: T, known: ReadonlySet<string> | null): T {
  const axisSafe = withKnownAxis(value, known)
  if (axisSafe !== value || !known || !value.majorId) return axisSafe
  const subId = value.subId ?? null
  const safe = legacySafeTaxonomy(value.axis ?? 'product', value.majorId, subId, known)
  if (safe.majorId === value.majorId && safe.subId === subId) return value
  return { ...value, majorId: safe.majorId, subId: safe.subId }
}
